import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  CaretDownIcon,
  MagnifyingGlassIcon,
  XIcon,
  PlusCircleIcon
} from "@phosphor-icons/react";

import { MarketplaceWrapperStyle } from "pages/Marketplace/styled";
import { CommonDashboardWrapper } from "styles/GlobalStyles";
import {
  CardFlexWrapperStyles,
  CardWrapperStyles,
  TableWrapperStyles
} from "pages/Marketplace/styled";

import DotOutlineFillIcon from "assets/SVGComponents/DotOutlineFillIcon";

import { formatCustomRange } from "utils/helper";
import { useMarketplaceSlice } from "pages/Marketplace/slice/index";

import { selectMarketplace } from "./slice/selector";

import SkeletonLoader from "components/PageLoader/SkeletonLoader";
import PillButtons from "components/Buttons/PillButtons";
import Input from "components/Input";
import LoadingSpinner from "components/PageLoader/Spinner";

import CommonTable from "components/Tables/CommonTable";
import columns from "pages/Marketplace/column";
import Button from "components/Buttons/index";
import Icon from "components/Icon/Icon";
import CustomDateModal from "components/CustomDateModal/index";

import { SERVICE_TYPES, statusLabelMap, statusPeriodMap } from "./data";

const Marketplace = () => {
  const dispatch = useDispatch();
  const { actions } = useMarketplaceSlice();
  const navigate = useNavigate();
  const {
    initialLoading,
    loading,
    marketplaces,
    filtered_count,
    pagination,
    pending_approvals,
    approval_requests,
    rejected_requests,
    filtered_pending_approvals,
    filtered_approval_requests,
    filtered_rejected_requests
  } = useSelector(selectMarketplace);

  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);

  const urlState = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      search: searchParams.get("search") || "",
      status: searchParams.get("status") || "",
      service: searchParams.get("service") || "",
      period: searchParams.get("period") || "",
      from_date: searchParams.get("from_date") || "",
      to_date: searchParams.get("to_date") || "",
      sort_by: searchParams.get("sort_by") || "",
      sort_order: searchParams.get("sort_order") || "asc"
    }),
    [searchParams]
  );

  const selectedStatusLabel = urlState.status
    ? statusLabelMap[urlState.status]
    : "All";

  const customRangeLabel =
    urlState.period === "custom_range" && urlState.from_date && urlState.to_date
      ? formatCustomRange([
          new Date(urlState.from_date),
          new Date(urlState.to_date)
        ])
      : null;

  const selectedPeriodLabel =
    customRangeLabel ||
    statusPeriodMap?.find((p) => p.value === urlState.period)?.label ||
    statusPeriodMap?.find((p) => p.value === "past_7_days")?.label;

  const selectedServiceLabel = urlState.service || "All";

  const statusMenuItems = [
    { label: "All", value: "" },
    {
      label: "Pending Approval",
      value: "pending_approval",
      count: filtered_pending_approvals
    },
    { label: "Approved", value: "approved", count: filtered_approval_requests },
    { label: "Rejected", value: "rejected", count: filtered_rejected_requests }
  ];

  const serviceMenuItems = [
    { label: "All", value: "" },
    ...SERVICE_TYPES.map((label) => ({ label, value: label }))
  ];

  const [searchTerm, setSearchTerm] = useState(urlState.search);
  const [debouncedSearch, setDebouncedSearch] = useState(urlState.search);
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [showDateModal, setShowDateModal] = useState(false);

  const syncURL = (updates = {}) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) return;
      if (value === "") params.delete(key);
      else params.set(key, value);
    });

    setSearchParams(params);
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      syncURL({ page: 1, search: debouncedSearch });
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  // Fetch marketplace whenever URL changes
  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const searchFromURL = searchParams.get("search") || "";
    const statusFromURL = searchParams.get("status") || "";
    const serviceFromURL = searchParams.get("service") || "";
    const sortByFromURL = searchParams.get("sort_by") || "";
    const sortOrderFromURL = searchParams.get("sort_order") || "asc";
    const periodFromURL = searchParams.get("period") || null;
    const from_dateFromURL = searchParams.get("from_date") || null;
    const to_dateFromURL = searchParams.get("to_date") || null;

    dispatch(
      actions.fetchMarketplace({
        page: pageFromURL,
        limit: pagination?.limit || 10,
        status: statusFromURL,
        service: serviceFromURL,
        search: searchFromURL,
        sort_by: sortByFromURL,
        sort_order: sortOrderFromURL,
        period: periodFromURL,
        from_date: from_dateFromURL,
        to_date: to_dateFromURL
      })
    );
  }, [searchParams]);

  const initialSortBy = searchParams.get("sort_by") || "";
  const initialSortOrder = searchParams.get("sort_order") || "asc";

  // Handlers
  const handleStatusSelect = (item) => {
    syncURL({ page: 1, status: item.value });
  };

  const handlePeriodSelect = (item) => {
    if (!item.value) {
      // Reset custom range
      setCustomDateRange([null, null]);
      syncURL({ page: 1, period: "", from_date: "", to_date: "" });
      return;
    }

    if (item.value === "custom_range") {
      setShowDateModal(true);
    } else {
      setCustomDateRange([null, null]);
      syncURL({ page: 1, period: item.value, from_date: "", to_date: "" });
    }
  };

  const handleServiceTypeSelect = (item) => {
    syncURL({ page: 1, service: item.value });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    syncURL({ page: 1, search: "" });
  };

  const handleSortChange = (column, order) => {
    syncURL({ sort_by: column, sort_order: order });
  };

  const handlePageChange = (newPage) => {
    syncURL({ page: newPage });
  };

  const handleCustomDateApply = (range) => {
    setCustomDateRange(range);

    syncURL({
      page: 1,
      period: "custom_range",
      from_date: format(range[0], "yyyy-MM-dd"),
      to_date: format(range[1], "yyyy-MM-dd")
    });

    setShowDateModal(false);
  };

  const tableColumns = useMemo(() => columns(), []);

  return (
    <>
      <MarketplaceWrapperStyle>
        <CommonDashboardWrapper>
          <div className="common-dashboard-wrapper">
            <h1 className="common-heading">Marketplace</h1>

            <Button
              leftIcon={
                <Icon
                  component={PlusCircleIcon}
                  format="outline"
                  weight="regular"
                />
              }
              onClick={() => navigate("/marketplace/create-campaign")}
            >
              Create Campaign
            </Button>
          </div>

          <CardFlexWrapperStyles>
            <CardWrapperStyles>
              <div className="card-list">
                <DotOutlineFillIcon color="#F5A000" />
                <span className="card-title">Pending Approval</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{pending_approvals}</p>
              )}
            </CardWrapperStyles>

            <CardWrapperStyles>
              <div className="card-list">
                <DotOutlineFillIcon color="#009336" />
                <span className="card-title">Approved Requests</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{approval_requests}</p>
              )}
            </CardWrapperStyles>

            <CardWrapperStyles>
              <div className="card-list">
                <DotOutlineFillIcon color="#D8001A" />
                <span className="card-title">Rejected Requests</span>
              </div>
              {initialLoading ? (
                <SkeletonLoader width="40px" />
              ) : (
                <p className="card-count">{rejected_requests}</p>
              )}
            </CardWrapperStyles>
          </CardFlexWrapperStyles>
        </CommonDashboardWrapper>

        <TableWrapperStyles>
          <div className="filter-bar">
            <div className="filter-bar-menus">
              <PillButtons
                placeholderText="Status"
                statusPlaceholder="statusActive"
                mainLabelText={selectedStatusLabel}
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                // activeRightIcon={<XCircleOutlineRegularIcon />}
                menuItems={statusMenuItems}
                onSelect={handleStatusSelect}
                size="M"
                maxWidth="max-content"
                customClassName="status-width"
              />

              <PillButtons
                placeholderText="Period"
                statusPlaceholder="statusActive"
                mainLabelText={selectedPeriodLabel}
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                // activeRightIcon={
                //   <Icon
                //     component={XCircleOutlineRegularIcon}
                //     format="outline"
                //     weight="regular"
                //   />
                // }
                menuItems={statusPeriodMap}
                onSelect={handlePeriodSelect}
                size="M"
              />

              <PillButtons
                placeholderText="Service"
                statusPlaceholder="statusActive"
                mainLabelText={selectedServiceLabel}
                rightIcon={
                  <Icon
                    component={CaretDownIcon}
                    format="outline"
                    weight="regular"
                  />
                }
                // activeRightIcon={<XCircleOutlineRegularIcon />}
                menuItems={serviceMenuItems}
                onSelect={handleServiceTypeSelect}
                size="M"
                maxWidth="max-content"
                customClassName="service-width"
              />
            </div>

            <Input
              id="searchCompany"
              type="text"
              placeholder="Search by User Phone Number"
              leftIcon={
                <Icon
                  component={MagnifyingGlassIcon}
                  format="outline"
                  weight="regular"
                />
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="S"
              className="search-input"
              rightIcon={
                loading ? (
                  <LoadingSpinner color="#1f1f1f" size={12} />
                ) : (
                  <Icon component={XIcon} format="outline" weight="regular" />
                )
              }
              handleClearSearch={handleClearSearch}
            />
          </div>

          <CommonTable
            data={marketplaces}
            columns={tableColumns}
            loading={loading}
            pagination={pagination}
            total_count={filtered_count}
            initialLoading={initialLoading}
            page={Number(searchParams.get("page")) || 1}
            limit={pagination?.limit || 10}
            onPageChange={handlePageChange}
            currentStatus={selectedStatusLabel}
            sort_by={initialSortBy}
            sort_order={initialSortOrder}
            onSortChange={handleSortChange}
          />
        </TableWrapperStyles>
      </MarketplaceWrapperStyle>

      {showDateModal && (
        <CustomDateModal
          show={showDateModal}
          showMonth={2}
          initialRange={customDateRange}
          onClose={() => setShowDateModal(false)}
          onApply={handleCustomDateApply}
        />
      )}
    </>
  );
};

export default Marketplace;
